import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { useApp } from '../context/AppContext';
import { useT } from '../lib/i18n';

async function fileToDataUrl(file, maxSize = 512) {
  const buf = await file.arrayBuffer();
  const blob = new Blob([buf], { type: file.type });
  const bitmap = await createImageBitmap(blob);
  const scale = Math.min(1, maxSize / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  c.getContext('2d').drawImage(bitmap, 0, 0, w, h);
  return c.toDataURL('image/jpeg', 0.85);
}

/**
 * ChildForm supports two modes:
 *  - create (no `initial` prop): POSTs a new child.
 *  - edit  (`initial` prop passed): PUTs an update to that child.
 * All fields set during creation are editable in edit mode.
 */
export default function ChildForm({ onClose, onSaved, onLimitReached = null, initial = null, showWelcome = false }) {
  const { uiLang } = useApp();
  const t = useT(uiLang);
  const isEdit = !!initial;
  const [name, setName] = useState(initial?.name || '');
  const [dob, setDob] = useState(initial?.dob || '');
  const [langs, setLangs] = useState(initial?.languages || ['en']);
  const [avatar, setAvatar] = useState(initial?.avatar || 'deer');
  const [avatarUrl, setAvatarUrl] = useState(initial?.avatar_url || null);
  const [ttsSpeed, setTtsSpeed] = useState(initial?.tts_speed ?? 0.75);
  const [voiceGender, setVoiceGender] = useState(initial?.voice_gender || 'female');
  // Iter-51.77 — child's actual gender (drives celebration-audio grammar
  // independent of voice picker) + broader learner category so
  // ReadBuddy supports adults (homemakers, working professionals, etc.)
  // — not just school students.
  const [gender, setGender] = useState(initial?.gender || null);
  const [category, setCategory] = useState(initial?.category || 'school');
  const [subLevel, setSubLevel] = useState(initial?.sub_level || '');
  const [languages, setLanguages] = useState([]);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/config/languages').then(({data}) => setLanguages(data));
  }, []);

  const toggleLang = (code) => setLangs(l => l.includes(code) ? l.filter(x => x !== code) : [...l, code]);
  const onAvatarFile = async (f) => { if (!f) return; setAvatarUrl(await fileToDataUrl(f)); };

  const submit = async () => {
    if (!name || !dob || langs.length === 0) return;
    // Iter-52.6 — Class field is now MANDATORY for school students so
    // the Starter Library can auto-suggest the right level. Adults
    // (homemaker/professional/business/other) and postgrad/UG skip
    // this — they use their category as the level signal instead.
    if (category === 'school' && !subLevel) {
      setErr(uiLang === 'hi'
        ? 'कृपया कक्षा चुनें।'
        : 'Please select a class.');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const body = { name, dob, languages: langs, avatar,
        avatar_url: avatarUrl, tts_speed: ttsSpeed, voice_gender: voiceGender,
        gender, category,
        // Sub-level only applies to school/UG/PG.
        sub_level: ['school','undergrad','postgrad'].includes(category) ? (subLevel || null) : null };
      if (isEdit) {
        await api.put(`/children/${initial.child_id}`, body);
        onSaved({ ...initial, ...body });
      } else {
        const { data } = await api.post('/children', body);
        onSaved(data);
      }
    } catch (e) {
      // Iter-51.66 — profile-limit hit (402). If a caller wired
      // onLimitReached, hand off to the parent so it can pop the
      // upgrade sheet instead of the form silently dying.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 402 && detail && typeof detail === 'object' && detail.code === 'profile_limit_reached' && onLimitReached) {
        onLimitReached(detail);
        return;
      }
      const msg = typeof detail === 'string' ? detail : (detail?.message || e?.message || 'Could not save profile.');
      setErr(msg);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full shadow-xl max-h-[90vh] overflow-y-auto">
        {/* Iter-52.6 — inline welcome greeting for brand-new signups.
            Renders ONLY when `showWelcome` is true (Profiles.jsx passes
            it based on children.length === 0 at first creation). NEVER
            blocks form interaction — the form below is immediately
            usable. Bilingual (Hindi / English) so both audiences feel
            welcomed on the same screen. */}
        {showWelcome && (
          <div data-testid="child-form-welcome" className="mb-5 -mx-2 bg-gradient-to-br from-[#FFF6EA] to-[#FFEDD5] border-2 border-[#FFD8B2] rounded-2xl p-4">
            <div className="font-extrabold text-[#FF6B1A] text-base leading-snug">
              Welcome to ReadBuddy! Thank you for starting your free trial.
            </div>
            <div className="text-sm text-[#5C3A17] mt-2 leading-snug">
              Full guides and information are available on your Profile page, in English and Hindi, anytime you need them.
            </div>
          </div>
        )}
        <h2 className="text-2xl font-extrabold mb-6">{isEdit ? 'Edit profile' : t('newChild')}</h2>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">{t('name')}</label>
            <input data-testid="child-name-input" value={name} onChange={e => setName(e.target.value)} spellCheck={false}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold text-lg" />
          </div>
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">{t('dob')}</label>
            <input data-testid="child-dob-input" type="date" value={dob} onChange={e => setDob(e.target.value)} className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold" />
          </div>
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">{t('languages')}</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {languages.map(l => (
                <button data-testid={`lang-chip-${l.code}`} key={l.code} onClick={() => toggleLang(l.code)}
                  className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${langs.includes(l.code) ? 'bg-[#2EC4B6] text-white border-[#2EC4B6]' : 'bg-white text-slate-600 border-slate-200'}`}>
                  {l.name}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">{t('voice')}</label>
            <div className="flex gap-2 mt-2">
              <button data-testid="voice-female" onClick={() => setVoiceGender('female')}
                className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${voiceGender==='female'?'bg-[#F472B6] text-white border-[#F472B6]':'bg-white text-slate-600 border-slate-200'}`}>{t('voiceFemale')}</button>
              <button data-testid="voice-male" onClick={() => setVoiceGender('male')}
                className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${voiceGender==='male'?'bg-[#38BDF8] text-white border-[#38BDF8]':'bg-white text-slate-600 border-slate-200'}`}>{t('voiceMale')}</button>
            </div>
          </div>
          {/* Iter-51.77 — child's actual gender. Drives celebration-audio
              grammar (feminine/masculine verb endings) independent of
              voice picker. "Prefer not to say" left null → defaults to
              feminine-grammar clips.
              Iter-52.30 — display labels renamed to "Female"/"Male" per
              user request (2026-02). Stored values REMAIN 'girl'/'boy'
              so existing children in Mongo keep working AND the
              celebration grammar helper (Quiz.jsx isFeminine()) — which
              already accepts both 'girl' and 'female' — is untouched. */}
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">Gender</label>
            <div className="flex gap-2 mt-2 flex-wrap">
              <button data-testid="gender-girl" onClick={() => setGender('girl')}
                className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${gender==='girl'?'bg-[#F472B6] text-white border-[#F472B6]':'bg-white text-slate-600 border-slate-200'}`}>Female</button>
              <button data-testid="gender-boy" onClick={() => setGender('boy')}
                className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${gender==='boy'?'bg-[#38BDF8] text-white border-[#38BDF8]':'bg-white text-slate-600 border-slate-200'}`}>Male</button>
              <button data-testid="gender-none" onClick={() => setGender(null)}
                className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${gender===null?'bg-slate-500 text-white border-slate-500':'bg-white text-slate-600 border-slate-200'}`}>Prefer not to say</button>
            </div>
          </div>
          {/* Iter-51.77 — two-level learner category. Adults (homemakers,
              working professionals, etc.) skip the sub-level. */}
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">I am a…</label>
            <select data-testid="child-category" value={category}
              onChange={e => { const c = e.target.value; setCategory(c); if (!['school','undergrad','postgrad'].includes(c)) setSubLevel(''); }}
              className="w-full mt-1 px-4 py-3 rounded-2xl border-2 border-slate-200 font-bold">
              <option value="school">School student</option>
              <option value="undergrad">Undergraduate</option>
              <option value="postgrad">Postgraduate</option>
              <option value="homemaker">Homemaker / Housewife</option>
              <option value="professional">Working Professional</option>
              <option value="business">Business Owner</option>
              <option value="other">Other</option>
            </select>
            {category === 'school' && (
              // Iter-52.22 — CRITICAL UX fix. The Class dropdown used
              // to render as an un-labelled bare `<select>` directly
              // under "I am a…", so parents didn't recognise it as
              // its own field ("the Class dropdown is not working /
              // not selectable"). We now break it out with a proper
              // CLASS heading + an explicit caret icon + a bright
              // orange focus ring so the parent immediately sees it
              // as an actionable, required field.
              <div className="mt-4">
                <label htmlFor="child-class-select" className="text-sm font-bold text-slate-600 uppercase tracking-wider block">
                  Class <span className="text-[#F97316]">*</span>
                </label>
                <div className="relative mt-1">
                  <select
                    id="child-class-select"
                    data-testid="child-sublevel-class"
                    value={subLevel}
                    onChange={e => setSubLevel(e.target.value)}
                    className={`w-full appearance-none px-4 py-3 pr-11 rounded-2xl border-2 font-bold bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F97316]/50 ${
                      subLevel ? 'border-slate-200' : 'border-[#F97316]'
                    }`}
                  >
                    <option value="">Select class…</option>
                    {Array.from({length:12}, (_, i) => (
                      <option key={i+1} value={`class_${i+1}`}>Class {i+1}</option>
                    ))}
                  </select>
                  {/* Chevron caret so the field visually reads as a
                      dropdown on mobile Safari, where native selects
                      can look nearly identical to text inputs. */}
                  <span aria-hidden="true"
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">
                    ▾
                  </span>
                </div>
                {!subLevel && (
                  <div className="text-[11px] font-bold text-[#F97316] mt-1">
                    Class is required — we use it to pick the right Starter Library level.
                  </div>
                )}
              </div>
            )}
            {(category === 'undergrad' || category === 'postgrad') && (
              <div className="mt-4">
                <label htmlFor="child-year-select" className="text-sm font-bold text-slate-600 uppercase tracking-wider block">Year</label>
                <div className="relative mt-1">
                  <select
                    id="child-year-select"
                    data-testid="child-sublevel-year"
                    value={subLevel}
                    onChange={e => setSubLevel(e.target.value)}
                    className="w-full appearance-none px-4 py-3 pr-11 rounded-2xl border-2 border-slate-200 font-bold bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#F97316]/50"
                  >
                    <option value="">Select year…</option>
                    {[1,2,3,4].map(y => (
                      <option key={y} value={`year_${y}`}>Year {y}</option>
                    ))}
                  </select>
                  <span aria-hidden="true"
                    className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-500 font-bold">
                    ▾
                  </span>
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">Avatar</label>
            <div className="flex gap-3 mt-2 items-center flex-wrap">
              {['deer','bear'].map(a => (
                <button key={a} onClick={() => { setAvatar(a); setAvatarUrl(null); }} data-testid={`avatar-${a}`}
                  className={`px-4 py-2 rounded-full font-bold ${avatar===a && !avatarUrl?'bg-[#FF6B1A] text-white':'bg-slate-100'}`}>
                  {a === 'deer' ? '🦌 Deer' : '🐻 Bear'}
                </button>
              ))}
              <label data-testid="custom-avatar-btn" className="px-4 py-2 rounded-full font-bold bg-slate-100 cursor-pointer">
                📷 {t('customAvatar')}
                <input type="file" accept="image/*" onChange={e => onAvatarFile(e.target.files?.[0])} className="hidden"/>
              </label>
              {avatarUrl && <img src={avatarUrl} alt="" className="w-12 h-12 rounded-full object-cover border-2 border-[#FF6B1A]"/>}
            </div>
          </div>
          <div>
            <label className="text-sm font-bold text-slate-600 uppercase tracking-wider">{t('readingSpeed')}</label>
            <div className="flex flex-wrap gap-2 mt-2">
              {/* Iter-51.97 — added "Very slow" (0.55x) as the new
                  slowest tier for beginning readers. Existing children
                  keep their saved speed (opt-in via this picker or the
                  Profile slider). Backend already handles 0.55 via the
                  el_speed 0.7 clamp + client_rate 0.786 compensation. */}
              {[{v:0.55,k:'verySlow'},{v:0.7,k:'slow'},{v:0.85,k:'normal'},{v:1.0,k:'fast'}].map(({v,k}) => (
                <button data-testid={`speed-${k}`} key={k} onClick={() => setTtsSpeed(v)}
                  className={`px-4 py-2 rounded-full font-bold text-sm border-2 ${Math.abs(ttsSpeed-v)<0.01?'bg-[#2EC4B6] text-white border-[#2EC4B6]':'bg-white text-slate-600 border-slate-200'}`}>{t(k)}</button>
              ))}
            </div>
          </div>
        </div>
        {err && (
          <div data-testid="child-form-error"
            className="mt-4 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 font-bold">
            {err}
          </div>
        )}
        <div className="flex gap-3 mt-8">
          <button onClick={onClose} data-testid="child-form-cancel" className="pushable bg-white border-2 border-slate-200 flex-1 px-6 py-3 rounded-full font-bold">{t('cancel')}</button>
          <button onClick={submit} disabled={saving} data-testid="child-form-save" className="pushable pushable-primary bg-[#FF6B1A] text-white flex-1 px-6 py-3 rounded-full font-bold disabled:opacity-60">
            {saving ? 'Saving…' : (isEdit ? 'Save changes' : t('save'))}
          </button>
        </div>
      </div>
    </div>
  );
}
